import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CampaignList from './CampaignList';
import { campaignService } from '../../services/campaignService';
import { referenceCodeTypeService } from '../../services/referenceCodeTypeService';


const CampaignDashboard = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewType, setViewType] = useState('quarter');
  const [expandedQuarters, setExpandedQuarters] = useState([]);
  const [campaignTypes, setCampaignTypes] = useState([]);

  useEffect(() => {
    fetchCampaigns();
    fetchCampaignTypes();
    initializeExpandedSections();
  }, [selectedYear, viewType]);

  const initializeExpandedSections = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    if (viewType === 'quarter') {
      // Determine current quarter
      let currentQuarter = 'Q1';
      if (currentMonth >= 3 && currentMonth <= 5) currentQuarter = 'Q2';
      else if (currentMonth >= 6 && currentMonth <= 8) currentQuarter = 'Q3';
      else if (currentMonth >= 9 && currentMonth <= 11) currentQuarter = 'Q4';
      
      setExpandedQuarters([currentQuarter]);
    } else {
      // For monthly view, expand current month
      const months = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];
      const currentMonthName = months[currentMonth];
      setExpandedQuarters([currentMonthName]);
    }
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const data = await campaignService.getCampaigns(selectedYear);
      setCampaigns(data);
    } catch (err) {
      setError('Failed to fetch campaigns');
      console.error('Error fetching campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignTypes = async () => {
    try {
      const data = await referenceCodeTypeService.getAllCampaignTypes();
      setCampaignTypes(data);
    } catch (err) {
      console.error('Error fetching campaign types:', err);
    }
  };

  const getQuarterlyCampaigns = () => {
    const quarterlyData = {
      Q1: [],
      Q2: [],
      Q3: [],
      Q4: []
    };

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    campaigns.forEach(campaign => {
      const startDate = new Date(campaign.scheduledTime);
      const month = startDate.getMonth();
      
      if (month >= 0 && month <= 2) quarterlyData.Q1.push(campaign);
      else if (month >= 3 && month <= 5) quarterlyData.Q2.push(campaign);
      else if (month >= 6 && month <= 8) quarterlyData.Q3.push(campaign);
      else if (month >= 9 && month <= 11) quarterlyData.Q4.push(campaign);
    });

    // Filter out future quarters
    const validQuarters = [];
    if (selectedYear < currentYear || currentMonth >= 0) validQuarters.push('Q1');
    if (selectedYear < currentYear || currentMonth >= 3) validQuarters.push('Q2');
    if (selectedYear < currentYear || currentMonth >= 6) validQuarters.push('Q3');
    if (selectedYear < currentYear || currentMonth >= 9) validQuarters.push('Q4');

    return { quarterlyData, validQuarters };
  };

  const getMonthlyCampaigns = () => {
    const monthlyData = {};
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Create months array from December to January (reverse order)
    const months = [
      'December', 'November', 'October', 'September', 'August', 'July',
      'June', 'May', 'April', 'March', 'February', 'January'
    ];

    // Only include months that are not in the future
    const validMonths = months.filter((month, index) => {
      const monthIndex = 11 - index; // Convert to 0-11 index
      return monthIndex <= currentMonth || selectedYear < currentYear;
    });

    validMonths.forEach(month => {
      monthlyData[month] = [];
    });

    campaigns.forEach(campaign => {
      const startDate = new Date(campaign.scheduledTime);
      const monthIndex = startDate.getMonth();
      const monthName = months[11 - monthIndex]; // Convert to reverse order
      if (monthlyData[monthName]) {
        monthlyData[monthName].push(campaign);
      }
    });

    return { monthlyData, validMonths };
  };

  const toggleQuarter = (quarter) => {
    setExpandedQuarters(prev => 
      prev.includes(quarter) 
        ? prev.filter(q => q !== quarter)
        : [...prev, quarter]
    );
  };

  const isQuarterInPast = (quarter) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    if (selectedYear < currentYear) return true;
    if (selectedYear > currentYear) return false;
    
    const quarterMonths = {
      'Q1': [0, 1, 2],   // Jan, Feb, Mar
      'Q2': [3, 4, 5],   // Apr, May, Jun
      'Q3': [6, 7, 8],   // Jul, Aug, Sep
      'Q4': [9, 10, 11]  // Oct, Nov, Dec
    };
    
    const quarterEndMonth = Math.max(...quarterMonths[quarter]);
    return currentMonth > quarterEndMonth;
  };

  const isMonthInPast = (monthName) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    if (selectedYear < currentYear) return true;
    if (selectedYear > currentYear) return false;
    
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const monthIndex = months.indexOf(monthName);
    return currentMonth > monthIndex;
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return '#6B7280'; // Gray
      case 'live':
      case 'active':
        return '#10B981'; // Green
      case 'scheduled':
        return '#3B82F6'; // Blue
      case 'paused':
        return '#F59E0B'; // Yellow
      case 'cancelled':
        return '#EF4444'; // Red
      default:
        return '#6B7280';
    }
  };

  // Check if there are any campaigns for the selected year
  const hasCampaignsForYear = () => {
    return campaigns && campaigns.length > 0;
  };

  const canCreateNewCampaign = () => {
    const currentYear = new Date().getFullYear();
    return selectedYear >= currentYear;
  };

  // Render no campaigns message
  const renderNoCampaignsMessage = () => {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-12 text-center">
          <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Campaigns Found</h3>
          <p className="text-gray-500 mb-6">
            There are no campaigns for the year {selectedYear}. 
            {selectedYear === new Date().getFullYear() ? ' Create your first campaign to get started!' : ' Try selecting a different year or create a new campaign.'}
          </p>
          <div className="flex justify-center space-x-4">
            {canCreateNewCampaign() && (
              <Link 
                to="/campaigns/new" 
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create New Campaign
              </Link>
            )}
            {selectedYear !== new Date().getFullYear() && (
              <button
                onClick={() => setSelectedYear(new Date().getFullYear())}
                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                View Current Year
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderQuarterlyView = () => {
    const { quarterlyData, validQuarters } = getQuarterlyCampaigns();
    const quarters = ['Q4', 'Q3', 'Q2', 'Q1'].filter(q => validQuarters.includes(q));

    return (
      <div className="space-y-4">
        {quarters.map(quarter => {
          const isPast = isQuarterInPast(quarter);
          return (
            <div key={quarter} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div 
                className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleQuarter(quarter)}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">
                    {expandedQuarters.includes(quarter) ? '▾' : '▸'}
                  </span>
                  <span className="font-semibold text-gray-900">{quarter}</span>
                </div>
              </div>
              
              {expandedQuarters.includes(quarter) && (
                <div className="px-4 pb-4">
                  {quarterlyData[quarter].length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="mb-4">No Campaigns available</p>
                      {!isPast && (
                        <Link 
                          to="/campaigns/new" 
                          className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
                        >
                          + Add Campaign
                        </Link>
                      )}
                    </div>
                  ) : (
                    <div>
                      <CampaignList 
                        campaigns={quarterlyData[quarter]} 
                        getStatusColor={getStatusColor}
                        onCampaignUpdate={fetchCampaigns}
                      />
                      {!isPast && (
                        <div className="mt-4 text-center">
                          <Link 
                            to="/campaigns/new" 
                            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
                          >
                            + Add Campaign
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMonthlyView = () => {
    const { monthlyData, validMonths } = getMonthlyCampaigns();

    return (
      <div className="space-y-4">
        {validMonths.map(month => {
          const isPast = isMonthInPast(month);
          return (
            <div key={month} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div 
                className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleQuarter(month)}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">
                    {expandedQuarters.includes(month) ? '▾' : '▸'}
                  </span>
                  <span className="font-semibold text-gray-900">{month}</span>
                </div>
              </div>
              
              {expandedQuarters.includes(month) && (
                <div className="px-4 pb-4">
                  {monthlyData[month].length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="mb-4">No Campaigns available</p>
                      {!isPast && (
                        <Link 
                          to="/campaigns/new" 
                          className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
                        >
                          + Add Campaign
                        </Link>
                      )}
                    </div>
                  ) : (
                    <div>
                      <CampaignList 
                        campaigns={monthlyData[month]} 
                        getStatusColor={getStatusColor}
                        onCampaignUpdate={fetchCampaigns}
                      />
                      {!isPast && (
                        <div className="mt-4 text-center">
                          <Link 
                            to="/campaigns/new" 
                            className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
                          >
                            + Add Campaign
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 text-lg">Loading campaigns...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500 text-lg">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-8 pt-8 pb-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 sm:mb-0">Campaign Management</h1>
          
          <div className="flex items-end space-x-4">
            {canCreateNewCampaign() && (
              <Link 
                to="/campaigns/new" 
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
              >
                + New Campaign
              </Link>
            )}
            
            <div className="flex items-center space-x-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Year</label>
                <select 
                  value={selectedYear} 
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">View</label>
                <select 
                  value={viewType} 
                  onChange={(e) => setViewType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="quarter">Quarter</option>
                  <option value="month">Month</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 px-8 pb-8">
        {hasCampaignsForYear() ? (
          viewType === 'quarter' ? renderQuarterlyView() : renderMonthlyView()
        ) : (
          renderNoCampaignsMessage()
        )}
      </div>
    </div>
  );
};

export default CampaignDashboard;
